import { Bell as BellIcon } from '@bnnvara/icons/src/components/UserInterfaceIcons/Bell';
import { useOnOutsideClick } from '@bnnvara/react-hooks';
import { useSound } from '@bnnvara/sound';
import { supportFormat } from '@bnnvara/sound/src/util/supportFormat';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';

import { store } from '../../../../helpers/store';
import { useNotifications } from '../../../../hooks/notifications';
import useUI, {
  TYPE_TOGGLE_NOTIFICATIONS_DROPDOWN,
} from '../../../../hooks/useUI';
import { browserNotifications } from './browserNotifications';
import { NotificationsDropdown } from './NotificationsDropdown';
import { SoundToggle } from './SoundToggle';
import * as S from './styled';

const NotificationsHub = () => {
  const settings = store('local', 'notifications');
  const [isMuted, setIsMuted] = useState(settings.getEntry('muted', false));
  const router = useRouter();
  const {
    isLoading,
    lastUpdate,
    notifications,
    unreadCount,
    setNotificationsOnRead,
  } = useNotifications();
  const browser = useRef(null);
  const {
    state: { notificationsDropdownOpen },
    dispatch,
  } = useUI();
  const playedSoundTimestamp = useRef(lastUpdate);
  const showNumberOfNotifications = unreadCount !== 0;
  const refDropdownContainer = useRef();
  const file = supportFormat('mp3')
    ? '/static/sounds/pop.mp3'
    : '/static/sounds/pop.ogg';
  const { play } = useSound(file);

  useEffect(() => {
    if (lastUpdate === playedSoundTimestamp.current.playedSoundTimestamp) {
      return;
    }

    playedSoundTimestamp.current = lastUpdate;

    if (!isMuted && unreadCount > 0) {
      play();
    }
  }, [lastUpdate, isMuted]);

  function toggleMuted() {
    const newValue = !isMuted;

    setIsMuted(newValue);
    settings.setEntry('muted', newValue);
  }

  /**
   * Put notification on read when a user click on hub and on a dropdownItem
   * Why dropdownItem? It is possible that the dropdown is open,
   * and that on that time a user receives a notification trough the websocket.
   */
  function clickHandler() {
    /**
     * Don't execute the mutation if there are 0 unread notifications,
     * to prevent unnecessary load in BFF and BE.
     * Also only execute if de dropDown is open, to assure
     * the dots (read or not read) of individual notifications is visible.
     */
    if (unreadCount > 0 && notificationsDropdownOpen) {
      setNotificationsOnRead();
    }

    dispatch({ type: TYPE_TOGGLE_NOTIFICATIONS_DROPDOWN });
  }

  useEffect(() => {
    if (browser.current === null) {
      browser.current = browserNotifications();
    }

    browser.current.setCount(unreadCount);
  }, [unreadCount]);

  /**
   * When the user navigates to an other page the title and favicon will be updated
   * and with that the notification indicators will be overwritten.
   * When the `routeChangeComplete` is fired the notification indicators are applied.
   */
  useEffect(() => {
    function update() {
      if (browser.current !== null) {
        browser.current.setCount(unreadCount);
      }
    }

    router.events.on('routeChangeComplete', update);

    return () => {
      router.events.off('routeChangeComplete', update);
    };
  }, []);

  useOnOutsideClick(refDropdownContainer, () => {
    if (notificationsDropdownOpen) {
      setNotificationsOnRead();

      dispatch({ type: TYPE_TOGGLE_NOTIFICATIONS_DROPDOWN });
    }
  });

  return (
    <div ref={refDropdownContainer}>
      <S.Wrapper>
        <S.IconButton
          className="js-notification-button"
          onClick={clickHandler}
          isDropdownOpen={notificationsDropdownOpen}
        >
          <BellIcon />
          {showNumberOfNotifications && (
            <S.NumberOfNotifications className="js-notification-text">
              {unreadCount}
            </S.NumberOfNotifications>
          )}
        </S.IconButton>
        {notificationsDropdownOpen && (
          <NotificationsDropdown
            isLoading={isLoading}
            notifications={notifications}
            setNotificationsOnRead={clickHandler}
          >
            <SoundToggle isMuted={isMuted} toggle={toggleMuted} />
          </NotificationsDropdown>
        )}
      </S.Wrapper>
    </div>
  );
};

export default NotificationsHub;
